/**
 * SkillWinnerShowcase — landing-page visual that conveys "closest answer wins"
 * (skill decides it), replacing the old spinning-names RaffleGlobe. There is NO
 * spinning, shuffling or "names being drawn" imagery — the point is that the
 * highest-scoring participant (the closest valid guess) wins by skill.
 *
 * The visual is a horizontal scale / number line with a TARGET marker (the
 * correct answer) and several participant guess markers. The closest guess is
 * highlighted as the "Winner". A small leaderboard, sorted by closeness, sits
 * below. Pure CSS — the only motion is a gentle pulse on the winner marker,
 * which is disabled under prefers-reduced-motion.
 *
 * Decorative + illustrative: the section has an accessible label and a caption
 * for context; the chart itself is aria-hidden, with the outcome conveyed in
 * the leaderboard text.
 */

// Example "Closest guess" round (illustrative only — not live data). The correct
// answer is TARGET; each participant submitted a guess. Skill (closeness) decides
// the winner — no luck, no random draw.
const TARGET = 68; // the correct answer, e.g. "How many sweets in the jar?"

type Guess = { name: string; value: number };

const GUESSES: Guess[] = [
  { name: 'Thabo', value: 42 },
  { name: 'Aisha', value: 91 },
  { name: 'Lerato', value: 70 }, // closest → winner
  { name: 'Sipho', value: 55 },
  { name: 'Naledi', value: 84 },
];

// Scale bounds for positioning markers along the line (0–100%).
const SCALE_MIN = 0;
const SCALE_MAX = 100;
const pctOf = (value: number) =>
  ((value - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 100;

// Rank guesses by closeness to the target — the winner is the smallest distance.
const ranked = [...GUESSES]
  .map((g) => ({ ...g, distance: Math.abs(g.value - TARGET) }))
  .sort((a, b) => a.distance - b.distance);

const WINNER = ranked[0];

const MARKER_COLORS = ['#7c3aed', '#ec4899', '#f97316'];

export function SkillWinnerShowcase() {
  return (
    <section
      className="relative bg-white py-12"
      aria-label="How the closest skill answer wins"
    >
      <style>{`
        @keyframes skill-winner-pulse {
          0%, 100% { transform: translate(-50%, -50%) scale(1); }
          50% { transform: translate(-50%, -50%) scale(1.12); }
        }
        .skill-winner-marker { animation: skill-winner-pulse 2.4s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) {
          .skill-winner-marker { animation: none !important; }
        }
      `}</style>

      <div className="mx-auto flex max-w-2xl flex-col items-center px-4">
        <h2 className="text-center font-heading text-xl font-bold text-violet-900 sm:text-2xl">
          Closest answer wins
        </h2>
        <p className="mt-2 max-w-md text-center text-sm text-gray-500">
          Everyone takes on the same skill challenge. The winner is the
          highest-scoring participant — the closest valid answer. Skill decides
          it, never luck.
        </p>

        {/* Number line / scale visual — illustrative, so aria-hidden. The
            outcome is also announced in the leaderboard below. */}
        <div
          className="relative mt-10 w-full rounded-2xl border border-violet-100 bg-violet-50/40 px-5 pb-14 pt-12 shadow-sm"
          aria-hidden="true"
        >
          {/* Target callout */}
          <div
            className="absolute -top-3 -translate-x-1/2 rounded-full bg-white px-3 py-1 text-xs font-bold text-violet-700 shadow ring-1 ring-violet-200"
            style={{ left: `${pctOf(TARGET)}%` }}
          >
            🎯 Correct answer
          </div>

          {/* The scale track */}
          <div
            className="relative h-2 w-full rounded-full"
            style={{
              background:
                'linear-gradient(90deg, #ede9fe 0%, #fce7f3 50%, #ffedd5 100%)',
            }}
          >
            {/* Target tick */}
            <div
              className="absolute top-1/2 h-8 w-0.5 -translate-x-1/2 -translate-y-1/2 rounded bg-violet-600"
              style={{ left: `${pctOf(TARGET)}%` }}
            />

            {/* Guess markers */}
            {GUESSES.map((g, i) => {
              const isWinner = g.name === WINNER.name;
              return (
                <div
                  key={g.name}
                  className={`absolute top-1/2 flex flex-col items-center ${
                    isWinner ? 'z-10' : ''
                  }`}
                  style={{ left: `${pctOf(g.value)}%` }}
                >
                  <span
                    className={`${
                      isWinner ? 'skill-winner-marker' : '-translate-x-1/2 -translate-y-1/2'
                    } block rounded-full ${
                      isWinner
                        ? 'h-6 w-6 ring-4 ring-white'
                        : 'h-3.5 w-3.5'
                    }`}
                    style={{
                      position: isWinner ? 'absolute' : 'relative',
                      left: isWinner ? '0' : undefined,
                      top: isWinner ? '0' : undefined,
                      background: isWinner
                        ? 'linear-gradient(135deg, #7c3aed 0%, #ec4899 100%)'
                        : MARKER_COLORS[i % MARKER_COLORS.length],
                    }}
                  />
                  <span
                    className={`mt-3 -translate-x-1/2 whitespace-nowrap rounded-full px-2 py-0.5 text-[11px] font-semibold ${
                      isWinner
                        ? 'bg-violet-600 text-white shadow'
                        : 'bg-white/80 text-gray-600'
                    }`}
                    style={{ position: 'absolute', top: '0.75rem' }}
                  >
                    {g.name}
                    {isWinner ? ' 🏆' : ''}
                  </span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Leaderboard — sorted by closeness (skill), the real outcome. This
            text is readable by screen readers (the chart above is decorative). */}
        <ol className="mt-8 w-full max-w-sm space-y-2">
          {ranked.map((g, i) => {
            const isWinner = i === 0;
            return (
              <li
                key={g.name}
                className={`flex items-center justify-between rounded-xl border px-4 py-2.5 text-sm ${
                  isWinner
                    ? 'border-violet-200 bg-violet-50 font-bold text-violet-900'
                    : 'border-gray-100 bg-white text-gray-600'
                }`}
              >
                <span className="flex items-center gap-2">
                  <span
                    className={`flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold ${
                      isWinner ? 'bg-violet-600 text-white' : 'bg-gray-100 text-gray-500'
                    }`}
                  >
                    {i + 1}
                  </span>
                  {g.name}
                  {isWinner && <span className="text-base">🏆</span>}
                </span>
                <span className={isWinner ? 'text-violet-700' : 'text-gray-400'}>
                  {isWinner
                    ? 'Winning participant'
                    : `${g.distance} off`}
                </span>
              </li>
            );
          })}
        </ol>

        <p className="mt-6 max-w-md text-center text-sm font-medium text-gray-500">
          Hundreds enter, the most skilful wins — decided by skill-based winner
          determination. Together, we can afford it.
        </p>
      </div>
    </section>
  );
}
